/**
 * Ranking service for tracking GMB positions across grid points
 */

import { GooglePlacesService } from './dataforseo-places';
import { GMBScoringService } from './gmb-scoring.js';
import { generateGridPoints, generateGridPointsWithAddresses, getGridLabel } from './geolocation';
import { RankingService as HeatmapRankingService } from '../../heatmap/lib/ranking-service';
import ApiService from '../../services/api';

import logger from '../../utils/logger';
export class RankingService {
  constructor(
    apiKey, 
    useMock = false, 
    useNewAPI = false,
    customSearchApiKey,
    searchEngineId,
    useLiveAPI = true
  ) {
    // Always use real Google Places API service with custom search integration
    this.placesService = new GooglePlacesService(
      apiKey, 
      useNewAPI,
      customSearchApiKey,
      searchEngineId
    );
    this.scoringService = new GMBScoringService();
    this.apiKey = apiKey;
    this.useNewAPI = useNewAPI;
    this.useLiveAPI = useLiveAPI; // Flag to use live API endpoint
    this.heatmapRankingService = new HeatmapRankingService(apiKey, useNewAPI ?? true, useLiveAPI);
  }

  // Map Smart Radius (km) -> grid size
  mapRadiusToGrid(radiusKm) {
    const r = Math.min(10, Math.max(1, Math.round(radiusKm)));
    const mapping = {
      1: 5,
      2: 6,
      3: 7,
      4: 8,
      5: 9,
      6: 10,
      7: 12,
      8: 14,
      9: 16,
      10: 20,
    };
    return mapping[r] || 5;
  }

  /**
   * Perform a complete ranking analysis with dynamic radius optimization
   */
  async performRankingAnalysis(
    config,
    onProgress
  ) {
    // Check if we should use smart radius optimization
    logger.log('🔍 ANALYSIS MODE CHECK:', {
      useSmartRadius: config.useSmartRadius,
      searchRadius: config.searchRadius,
      businessName: config.businessName
    });
    
    if (config.useSmartRadius) {
      logger.log('✅ TRIGGERING SMART RADIUS ANALYSIS');
      return this.performSmartRadiusAnalysis(config, onProgress);
    }
    
    logger.log('📊 TRIGGERING STANDARD ANALYSIS');
    return this.performStandardAnalysis(config, onProgress);
  }

  /**
   * Smart radius analysis - expands radius based on Competitive Ranking Leaderboard position
   */
  async performSmartRadiusAnalysis(
    config,
    onProgress
  ) {
    logger.log('🎯 Starting Smart Radius Analysis...');
    logger.log('   Strategy: Expand radius until Competitive Ranking Leaderboard position drops to rank 8 or below');
    logger.log('   Max radius: 10km');
    logger.log('   useSmartRadius flag:', config.useSmartRadius);
    logger.log('   Original config radius:', config.searchRadius);
    
    let bestResult = null;
    let previousLeaderboardPosition = null;
    let currentRadius = 1; // Always start with 1km
    const maxRadius = 10; // Maximum 10km
    const stopAtRank = 8; // Stop when rank drops to 8 or below (worse)
    const radiusResults = []; // Track results for each radius
    
    // Start radius expansion from 1km
    let shouldContinue = true;
    
    logger.log(`🚀 Starting radius expansion loop from ${currentRadius}km to max ${maxRadius}km`);
    
    while (currentRadius <= maxRadius && shouldContinue) {
      logger.log(`🔍 Testing radius: ${currentRadius}km (shouldContinue: ${shouldContinue})`);
      
      const gridSizeForRadius = this.mapRadiusToGrid(currentRadius);
      const pinCount = gridSizeForRadius * gridSizeForRadius;
      if (onProgress) {
        const progress = (currentRadius / maxRadius) * 90;
        onProgress(progress, 100, 'Analyzing competitors...');
      }
      
      const testConfig = { ...config, searchRadius: currentRadius, gridSize: gridSizeForRadius };
      logger.log(`📐 SmartRadius: radius=${currentRadius}km -> grid=${gridSizeForRadius}×${gridSizeForRadius} (${pinCount} pins)`);
      const result = await this.performStandardAnalysis(testConfig, (current, total, status) => {
        if (onProgress) {
          const baseProgress = (currentRadius / maxRadius) * 90;
          const currentProgress = (current / total) * (90 / maxRadius);
          onProgress(baseProgress + currentProgress, 100, 'Analyzing competitors...');
        }
      });
      
      const leaderboardPosition = this.calculateLeaderboardPosition(result);
      const rankText = leaderboardPosition ? `Rank ${leaderboardPosition}` : 'Not found';
      
      // Show progress alert with radius completion and competitive rank
      if (leaderboardPosition) {
        if (leaderboardPosition >= stopAtRank) {
          const alertMessage = `🎯 ${currentRadius}km Complete · grid ${gridSizeForRadius}×${gridSizeForRadius} (${pinCount} pins) · Leader Rank: ${leaderboardPosition} — Stopping (≤8 reached)`;
          radiusResults.push(`For ${currentRadius} km: ${rankText} (Stop expanding)`);
          logger.log(`📊 For ${currentRadius} km: ${rankText} (Stop expanding)`);
          logger.log(`🚨 ALERT: ${alertMessage}`);
          
          // Update progress with alert message
          if (onProgress) {
            onProgress(95, 100, 'Finalizing analysis...');
          }
          
          bestResult = result;
          shouldContinue = false;
        } else {
          const alertMessage = `🎯 ${currentRadius}km Complete · grid ${gridSizeForRadius}×${gridSizeForRadius} (${pinCount} pins) · Leader Rank: ${leaderboardPosition} — Expanding to ${currentRadius + 1}km`;
          radiusResults.push(`For ${currentRadius} km: ${rankText} (Continue expanding)`);
          logger.log(`📊 For ${currentRadius} km: ${rankText} (Continue expanding)`);
          logger.log(`🚨 ALERT: ${alertMessage}`);
          
          // Update progress with alert message
          if (onProgress) {
            const nextProgress = ((currentRadius + 1) / maxRadius) * 90;
            onProgress(nextProgress, 100, 'Analyzing competitors...');
          }
          
          bestResult = result;
          previousLeaderboardPosition = leaderboardPosition;
        }
      } else {
        const alertMessage = `🎯 ${currentRadius}km Complete · grid ${gridSizeForRadius}×${gridSizeForRadius} (${pinCount} pins) · Leader Rank: Not Found — Expanding to ${currentRadius + 1}km`;
        radiusResults.push(`For ${currentRadius} km: Not found (Continue expanding)`);
        logger.log(`📊 For ${currentRadius} km: Not found (Continue expanding)`);
        logger.log(`🚨 ALERT: ${alertMessage}`);
        
        // Update progress with alert message
        if (onProgress) {
          const nextProgress = ((currentRadius + 1) / maxRadius) * 90;
          onProgress(nextProgress, 100, 'Analyzing competitors...');
        }
        
        if (!bestResult) bestResult = result;
      }
      
      // Add a small delay to show the alert message
      await this.delay(1500); // 1.5 second delay to show alert
      
      // Only increment if we're continuing
      if (shouldContinue && currentRadius < maxRadius) {
        currentRadius++;
        logger.log(`🔄 Incrementing to next radius: ${currentRadius}km`);
      } else {
        logger.log(`🛑 Not incrementing - shouldContinue: ${shouldContinue}, currentRadius: ${currentRadius}, maxRadius: ${maxRadius}`);
        break;
      }
    }
    
    // Display all results
    logger.log(`🏁 Smart radius analysis complete!`);
    logger.log(`📋 Results Summary:`);
    radiusResults.forEach(result => logger.log(`   ${result}`));
    
    const finalRadius = bestResult?.searchRadius || 1;
    const finalLeaderboardPosition = this.calculateLeaderboardPosition(bestResult);
    
    if (onProgress) {
      const finalGrid = this.mapRadiusToGrid(finalRadius);
      onProgress(100, 100, 'Analysis complete!');
    }
    
    return bestResult;
  }

  /**
   * Calculate leaderboard position for smart radius stopping condition
   */
  calculateLeaderboardPosition(rankingData) {
    // Get all businesses and their average rankings with 50 competitor limit
    const competitorCounts = new Map();
    const competitorPositions = new Map();
    const competitorBeatYou = new Map();
    
    // Count competitor appearances and track their positions with early termination at 50 competitors
    const yourPlaceId = rankingData.targetPlaceId;
    let competitorCount = 0;
    const maxCompetitors = 50;
    // Use placeId for uniqueness in stop condition
    const seenCompetitorIds = new Set();
    
    outerLoop: for (const result of rankingData.results) {
      const yourRank = result.targetBusinessRank;
      
      for (const [index, business] of result.businesses.entries()) {
        const rank = index + 1;
        
        const isYou = yourPlaceId ? (business.placeId === yourPlaceId) : false;
        if (!isYou) {
          // Check if this is a new competitor by placeId (robust uniqueness)
          if (!seenCompetitorIds.has(business.placeId)) {
            if (seenCompetitorIds.size >= maxCompetitors) {
              break outerLoop; // Stop processing once we reach 50 unique competitors
            }
            seenCompetitorIds.add(business.placeId);
            competitorCount++;
          }
          
          competitorCounts.set(business.name, (competitorCounts.get(business.name) || 0) + 1);
          if (!competitorPositions.has(business.name)) {
            competitorPositions.set(business.name, []);
            competitorBeatYou.set(business.name, 0);
          }
          competitorPositions.get(business.name).push(rank);
          
          // Check if competitor beat you in this location
          if (yourRank && rank < yourRank) {
            competitorBeatYou.set(business.name, (competitorBeatYou.get(business.name) || 0) + 1);
          }
        }
      }
    }
    
    // Get top competitors with enhanced metrics (EXACT SAME AS UI)
    const topCompetitors = Array.from(competitorCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15) // Get more competitors for leaderboard
      .map(([name, appearances]) => {
        const positions = competitorPositions.get(name) || [];
        const avgPosition = positions.length > 0 ? positions.reduce((a, b) => a + b, 0) / positions.length : 0;
        const timesBeatsYou = competitorBeatYou.get(name) || 0;
        const bestRank = positions.length > 0 ? Math.min(...positions) : null;
        const coverage = (appearances / rankingData.results.length) * 100;
        
        return {
          name,
          appearances,
          averageRank: avgPosition,
          bestRank,
          timesBeatsYou,
          coverage,
          totalGridPoints: rankingData.results.length,
          threatLevel: timesBeatsYou > appearances * 0.7 ? 'high' : timesBeatsYou > appearances * 0.4 ? 'medium' : 'low'
        };
      });
    
    // Create competitive leaderboard including your business (EXACT SAME AS UI)
    const yourAverageRank = rankingData.averageRank || 999;
    const allCompetitorsWithYou = [
      ...topCompetitors.map(comp => ({
        name: comp.name,
        averageRank: comp.averageRank,
        isYourBusiness: false,
        appearances: comp.appearances,
        coverage: comp.coverage
      })),
      {
        name: rankingData.businessName,
        averageRank: yourAverageRank,
        isYourBusiness: true,
        appearances: rankingData.results.length - rankingData.notRankedCount,
        coverage: ((rankingData.results.length - rankingData.notRankedCount) / rankingData.results.length) * 100
      }
    ].sort((a, b) => a.averageRank - b.averageRank); // Sort by average rank (best to worst)
    
    // Find your position in the leaderboard
    const yourPosition = allCompetitorsWithYou.findIndex(b => b.isYourBusiness) + 1;
    
    return yourPosition;
  }

  /**
   * Standard ranking analysis (original method)
   */
  async performStandardAnalysis(
    config,
    onProgress
  ) {
    logger.log('🎯 Starting standard ranking analysis (heatmap-aligned)...');
    logger.log('🔍 [RANKING SERVICE DEBUG] Full config received:', config);
    logger.log('🔍 [RANKING SERVICE DEBUG] Using Live API:', this.useLiveAPI);

    // If live API is enabled, use it directly
    if (this.useLiveAPI) {
      return this.performLiveAPIAnalysis(config, onProgress);
    }

    // Otherwise, use heatmap ranking service (Google Places API)
    const heatmapResult = await this.heatmapRankingService.performRankingAnalysis(
      {
        ...config,
        targetPlaceId: config.targetPlaceId,
        businessName: config.businessName
      },
      onProgress
    );

    return {
      ...heatmapResult,
      targetPlaceId: config.targetPlaceId ?? heatmapResult.targetPlaceId,
      searchRadius: config.searchRadius ?? heatmapResult.searchRadius
    };
  }

  /**
   * Perform competitor analysis using live backend API
   */
  async performLiveAPIAnalysis(config, onProgress) {
    logger.log('🚀 Using Live API endpoint for competitor analysis');
    
    try {
      const keyword = typeof config?.keyword === 'string' ? config.keyword.trim() : '';
      if (!keyword) {
        throw new Error('keyword required');
      }

      // Prepare request payload for live API (DataForSEO format with data wrapper)
      const requestData = {
        data: [{
          keyword,
          language_code: "en",
          location_coordinate: `${config.centerLocation?.lat},${config.centerLocation?.lng},17z`,
          device: "desktop",
          os: "windows",
          max_crawl_pages: 2,
          search_this_area: false,
          search_places: true,
          tag: `nearby-${config.centerLocation?.lat}-${config.centerLocation?.lng}`,
          depth: 20
        }]
      };

      logger.log('📤 Sending to live API:', requestData);

      // Call live API
      const response = await ApiService.runMaps(requestData);

      logger.log('📥 Received response from live API:', response);

      // Transform response to match expected format
      const transformedResult = this.transformLiveAPIResponse(response, config);

      if (onProgress) onProgress(100, 100, 'Complete!');

      return transformedResult;
    } catch (error) {
      logger.error('❌ Live API error:', error);
      
      // Fallback to heatmap ranking service if live API fails
      logger.warn('⚠️ Falling back to Google Places API');
      
      const heatmapResult = await this.heatmapRankingService.performRankingAnalysis(
        {
          ...config,
          targetPlaceId: config.targetPlaceId,
          businessName: config.businessName
        },
        onProgress
      );

      return {
        ...heatmapResult,
        targetPlaceId: config.targetPlaceId ?? heatmapResult.targetPlaceId,
        searchRadius: config.searchRadius ?? heatmapResult.searchRadius
      };
    }
  }

  /**
   * Transform live API response to match expected format
   */
  transformLiveAPIResponse(response, config) {
    logger.log('🔄 Transforming live API response:', response);
    
    // Check if response has data wrapper (DataForSEO API pattern)
    const apiData = response.data || response;
    
    // If response already has the expected structure, return it
    if (apiData.results && apiData.trackingId) {
      logger.log('✅ Response already in correct format');
      return apiData;
    }

    // Extract data from DataForSEO nested structure
    logger.log(`📊 Processing ${apiData.tasks?.length || 0} tasks from backend`);

    // Generate tracking ID
    const trackingId = `tracking_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const timestamp = new Date();

    // Reconstruct centerLocation from lat/lng if needed
    const centerLocation = {
      lat: config.centerLocation?.lat,
      lng: config.centerLocation?.lng
    };

    // Create grid results structure with proper coordinates for each grid point
    const gridSize = config.gridSize || 4;
    const searchRadius = config.searchRadius || 2;
    
    // Generate actual grid points with unique coordinates for each position
    const gridPoints = generateGridPoints(centerLocation, searchRadius, gridSize);
    
    // Process each task (one per grid point) with its unique results
    const gridResults = [];
    
    if (apiData.tasks && apiData.tasks.length > 0) {
      apiData.tasks.forEach((task, taskIndex) => {
        const taskData = task.data || {};
        const gridPosition = taskData.grid_position;
        
        // Extract businesses from this specific task
        let businesses = [];
        if (task.result && task.result.length > 0) {
          businesses = task.result[0].items || [];
        }
        
        // Transform businesses for this grid point
        const transformedBusinesses = businesses.map((item, index) => ({
          name: item.title,
          placeId: item.place_id,
          place_id: item.place_id,
          address: item.address,
          rating: item.rating?.value,
          reviewCount: item.rating?.votes_count,
          latitude: item.latitude,
          longitude: item.longitude,
          phone: item.phone,
          website: item.url,
          category: item.category,
          categories: [item.category, ...(item.additional_categories || [])],
          types: item.category_ids || [],
          photoCount: item.total_photos,
          workHours: item.work_hours,
          rank: item.rank_absolute || index + 1,
          originalRank: item.rank_absolute || index + 1,
          searchMethod: 'dataforseo_live_api'
        }));
        
        // Find corresponding grid point
        let gridPoint;
        if (gridPosition) {
          gridPoint = {
            lat: gridPosition.lat,
            lng: gridPosition.lng,
            id: gridPosition.cellId,
            gridX: gridPosition.j,
            gridY: gridPosition.i,
          };
        } else {
          gridPoint = gridPoints[taskIndex] || gridPoints[0];
        }
        
        const targetRank = this.findBusinessRank(transformedBusinesses, config.businessName);
        
        gridResults.push({
          gridPoint: {
            lat: gridPoint.lat,
            lng: gridPoint.lng,
            id: gridPoint.id,
            gridX: gridPoint.gridX,
            gridY: gridPoint.gridY,
            address: config.businessAddress || 'Center Location',
            label: `${gridPoint.gridY},${gridPoint.gridX}`
          },
          businesses: transformedBusinesses,
          targetBusinessRank: targetRank,
          searchRadius: searchRadius * 1000,
          timestamp,
          keyword: taskData?.keyword || config.keyword
        });
        
        logger.log(`  ✅ Grid ${gridPoint.id}: ${businesses.length} businesses, rank: ${targetRank || 'Not found'}`);
      });
    } else {
      logger.warn('⚠️ No tasks from backend, using empty grid');
      gridPoints.forEach((gridPoint) => {
        gridResults.push({
          gridPoint: {
            lat: gridPoint.lat,
            lng: gridPoint.lng,
            id: gridPoint.id,
            gridX: gridPoint.gridX,
            gridY: gridPoint.gridY,
            address: config.businessAddress || 'Center Location',
            label: `${gridPoint.gridY},${gridPoint.gridX}`
          },
          businesses: [],
          targetBusinessRank: null,
          searchRadius: searchRadius * 1000,
          timestamp,
          keyword: config.keyword
        });
      });
    }

    const transformed = {
      trackingId,
      businessName: config.businessName,
      centerLocation,
      keyword: apiData.tasks?.[0]?.data?.keyword || config.keyword,
      gridSize: config.gridSize,
      searchRadius: config.searchRadius,
      results: gridResults,
      timestamp,
      averageRank: null,
      bestRank: null,
      worstRank: null,
      notRankedCount: 0,
      coverage: 0,
      targetPlaceId: config.targetPlaceId
    };

    logger.log('🔄 Transformed result:', {
      resultsCount: transformed.results?.length || 0,
      totalGridPoints: transformed.results?.length || 0,
      trackingId: transformed.trackingId,
      gridSize: transformed.gridSize
    });

    return transformed;
  }

  findBusinessRank(businesses, targetBusinessName) {
    if (!targetBusinessName) return null;
    const normalize = (s) => {
      if (!s || typeof s !== 'string') return '';
      return s.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    };
    const target = normalize(targetBusinessName);
    if (!target) return null;
    
    for (let i = 0; i < businesses.length; i++) {
      const name = normalize(businesses[i]?.name || businesses[i]?.title || '');
      if (!name) continue;
      if (name === target || name.includes(target) || target.includes(name)) return i + 1;
    }
    return null;
  }

  // Auto-detect user's business primary category
  async detectUserBusinessCategory(config) {
    try {
      let userBusiness = null;
      logger.log('🔍 [DEBUG] Starting category detection with config:', config);

      // Method 1: Try to find by Place ID first (most accurate)
      if (config.targetPlaceId) {
        logger.log(`🔍 Looking up business by Place ID: ${config.targetPlaceId}`);
        try {
          userBusiness = await this.placesService.getPlaceDetailsForEnrichment(config.targetPlaceId);
          logger.log('🔍 Found business by Place ID:', userBusiness?.name);
          logger.log('🔍 [DEBUG] Business data from Place ID:', userBusiness);
        } catch (error) {
          logger.warn('🔍 Place ID lookup failed:', error.message);
        }
      }

      // Method 2: Search by business name and location if Place ID failed
      if (!userBusiness && config.businessName && config.centerLocation) {
        logger.log(`🔍 Searching for business: ${config.businessName}`);
        try {
          const searchResults = await this.placesService.enhancedSearch(
            config.centerLocation,
            0.5, // Small radius to find exact business
            config.businessName,
            config.businessName
          );
          
          // Find the best match by name
          userBusiness = searchResults.find(business => 
            business.name.toLowerCase().includes(config.businessName.toLowerCase()) ||
            config.businessName.toLowerCase().includes(business.name.toLowerCase())
          );
          
          if (userBusiness) {
            logger.log('🔍 Found business by search:', userBusiness.name);
            logger.log('🔍 [DEBUG] Business data from search:', userBusiness);
          } else {
            logger.warn('🔍 No matching business found in search results');
            logger.log('🔍 [DEBUG] Search results:', searchResults);
          }
        } catch (error) {
          logger.warn('🔍 Business name search failed:', error.message);
        }
      }

      // Extract primary category from the found business
      if (userBusiness) {
        let primaryCategory = null;

        // Priority 1: primaryType (most specific)
        if (userBusiness.primaryType) {
          primaryCategory = userBusiness.primaryType;
          logger.log(`🏷️ Using primaryType: ${primaryCategory}`);
        }
        // Priority 2: First category in categories array
        else if (userBusiness.categories && userBusiness.categories.length > 0) {
          primaryCategory = userBusiness.categories[0];
          logger.log(`🏷️ Using first category: ${primaryCategory}`);
        }
        // Priority 3: First type in types array
        else if (userBusiness.types && userBusiness.types.length > 0) {
          primaryCategory = userBusiness.types[0];
          logger.log(`🏷️ Using first type: ${primaryCategory}`);
        }

        if (primaryCategory) {
          // Clean up the category (remove underscores, etc.)
          const cleanedCategory = primaryCategory.replace(/_/g, ' ').toLowerCase();
          logger.log(`🏷️ Raw category: "${primaryCategory}" → Cleaned: "${cleanedCategory}"`);
          logger.log(`✅ SUCCESS: Your business category detected as "${cleanedCategory}"`);
          return cleanedCategory;
        }
      }

      logger.warn('🔍 Could not detect primary category for your business');
      return null;
    } catch (error) {
      logger.error('🔍 Error detecting business category:', error);
      return null;
    }
  }

  // Filter businesses by primary category (EXACT MATCH)
  filterByPrimaryCategory(businesses, targetCategory) {
    if (!targetCategory || !Array.isArray(businesses)) return businesses;

    const normalizedTarget = targetCategory.toLowerCase().trim();
    
    return businesses.filter(business => {
      // Check primary type first (EXACT MATCH)
      if (business.primaryType) {
        const normalizedPrimary = business.primaryType.toLowerCase().trim();
        if (normalizedPrimary === normalizedTarget) {
          return true;
        }
      }

      // Check categories/types array (EXACT MATCH)
      if (business.categories && Array.isArray(business.categories)) {
        return business.categories.some(category => {
          if (!category || typeof category !== 'string') return false;
          const normalizedCategory = category.toLowerCase().trim();
          return normalizedCategory === normalizedTarget;
        });
      }

      // Check types array (fallback - EXACT MATCH)
      if (business.types && Array.isArray(business.types)) {
        return business.types.some(type => {
          if (!type || typeof type !== 'string') return false;
          const normalizedType = type.toLowerCase().trim();
          return normalizedType === normalizedTarget;
        });
      }

      return false;
    });
  }

  // Determine the rank of the target business (by placeId or name match)
  computeTargetBusinessRank(businesses, config) {
    if (!Array.isArray(businesses) || businesses.length === 0) return null;

    // Prefer placeId if provided
    if (config && config.targetPlaceId) {
      const idx = businesses.findIndex(b => b.placeId === config.targetPlaceId);
      if (idx >= 0) return idx + 1;
    }

    // Fallback to name matching
    if (config && config.businessName) {
      const target = (config.businessName || '').toLowerCase().trim();
      if (target) {
        const idxByName = businesses.findIndex(b => (b.name || '').toLowerCase().includes(target));
        if (idxByName >= 0) return idxByName + 1;
      }
    }

    return null;
  }

  // Simple delay utility
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  generateTrackingId() {
    return 'track_' + Math.random().toString(36).substr(2, 9);
  }
}

